import React, { useState } from "react";

import { PlusGrayIcon } from "../../../../CommonUI/Icon/PlusGrayIcon";

import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import {
    RuleDesignerRender,
    RuleEditorInTester,
    RuleEditorRender,
    RuleLabelColumn,
    RuleTableEmptyAddColumn,
    RuleTableEmptyColumn,
    RuleTableEmptyIndexColumn,
    RuleToolbarRender,
} from "../../../DesignUI/CommonStyles";

import { Notification } from "../../../../CommonUI/BuilderUI/Notification";

import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { ValueTableRuleEditContext } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableRuleEditContext";

import { LinkingDataDialog } from "../../../DesignUI/LinkingDataDialog";
import { SettingButton } from "../../../DesignUI/SettingButton";

import { ValueRuleTableHeader } from "./ValueTableRuleHeader";
import { ValueTableResultDefaultCell } from "./ValueTableResultDefaultCell";
import { DockedSidebar } from "../../../../CommonUI/BuilderUI/DockSidebar";
import { ValidationInformation } from "../../../DesignUI/ValidationInformation";
import { ObserveMultiState } from "../../../../CommonUI/StateManagement/ObserveMultiState";

import { XValueTableRow } from "./ValueTableRow";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";
import {
    ITableRowTestValue,
    TableRowTestContext,
} from "../../../../rule-engine-core/Rule/Table/Common/TableRowTestContext";
import { XResultCell } from "../../CommonUI/XResultCell";
import { PassedTestRow } from "../../CommonStyles";
import { TestResult } from "../../../../CommonUI/Testing/TestResult";
import { ConfirmDialog } from "../../../../CommonUI/ConfirmDialog";
import { DataUtilities } from "../../../../rule-engine-core/Common/DataUtilities";
import { UILabel } from "../../../../CommonUI/BuildlingBlocks/UILabel";
import { UISpacedBoxContent } from "../../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { PropertyEditorBoolean } from "../../../../CommonUI/PropertyEditing/PropertyEditorBoolean";

interface IValueTableRuleDesigner {
    editContext: ValueTableRuleEditContext;
    ruleDefinition: ValueTableConfiguration;
}

export const ValueTableRuleDesigner = (props: IValueTableRuleDesigner) => {
    const { ruleDefinition, editContext } = props;
    const [openSettingsLayer, setOpenSettingsLayer] = useState<boolean>(false);

    const testContext = React.useContext(TestContext);

    const isDefaultTestResult =
        !!testContext &&
        !testContext.isError &&
        !(testContext?.items || []).find((rowResult) => rowResult.result === "true");

    const onAddNewRow = () => {
        ruleDefinition.addRow();
        StateManager.propertyChanged(ruleDefinition, "rows");
    };

    if (testContext) {
        return (
            <RuleEditorInTester>
                <RuleEditorRender
                    columnCount={ruleDefinition.columns.length + ruleDefinition.resultColumns.length + 1}
                    rowsGap={6}
                    className="RuleEditorRender"
                >
                    <ValueRuleTableHeader {...props} />

                    {ruleDefinition.rows.map((row, idx) => {
                        return (
                            <XValueTableRow
                                row={row}
                                index={idx}
                                key={row._id}
                                ruleDefinition={ruleDefinition}
                                editContext={editContext}
                            />
                        );
                    })}
                    <RuleTableEmptyIndexColumn
                        onClick={onAddNewRow}
                        data-testid="btn-value-table-add-new-row"
                    />
                    {ruleDefinition.columns.map((_, idx) => (
                        <RuleTableEmptyColumn key={idx} />
                    ))}

                    <RuleTableEmptyAddColumn />

                    {ruleDefinition.resultColumns.map((col) => {
                        return <RuleTableEmptyColumn key={col._id} />;
                    })}

                    <RuleTableEmptyAddColumn />
                    <RuleTableEmptyAddColumn />
                    {ruleDefinition.allowDefaultResults && (
                        <TableRowTestContext.Provider
                            value={isDefaultTestResult ? ITableRowTestValue.PASSED : undefined}
                        >
                            {isDefaultTestResult && (
                                <PassedTestRow
                                    data-testid="passedtestrow"
                                    x={ruleDefinition.columns.length + 3}
                                    y={ruleDefinition.rows.length + 4}
                                    columnsSpan={ruleDefinition.resultColumns.length}
                                />
                            )}
                            <RuleLabelColumn
                                colSpan={ruleDefinition.columns.length + 1}
                                colStart={1}
                                y={ruleDefinition.rows.length + 4}
                            >
                                Default Result Values
                            </RuleLabelColumn>

                            {ruleDefinition.resultColumns.map((rescol, index2) => {
                                return (
                                    <XResultCell
                                        ruleDefinition={ruleDefinition}
                                        resultColumn={rescol}
                                        resultCell={ruleDefinition.defaultResult[rescol._id]}
                                        editContext={editContext}
                                        key={rescol._id}
                                        rowIndex={ruleDefinition.rows.length + 4}
                                        cellIndex={ruleDefinition.columns.length + 2 + index2}
                                    />
                                );
                            })}
                        </TableRowTestContext.Provider>
                    )}
                </RuleEditorRender>
                <TestResult executableId={ruleDefinition._id} />
            </RuleEditorInTester>
        );
    }

    return (
        <RuleDesignerRender>
            <RuleToolbarRender>
                <SettingButton onClick={() => setOpenSettingsLayer(true)} />
            </RuleToolbarRender>
            <DockedSidebar
                sidebar={() => {
                    return (
                        <UISpacedBoxContent>
                            <UILabel>Settings</UILabel>
                            <ObserveState
                                listenTo={ruleDefinition}
                                control={() => (
                                    <PropertyEditorBoolean
                                        editObject={ruleDefinition}
                                        propertyName={"allowDefaultResults"}
                                        label={"Allow Default Results"}
                                    />
                                )}
                            />
                        </UISpacedBoxContent>
                    );
                }}
                content={() => (
                    <ObserveState
                        listenTo={ruleDefinition}
                        properties={["columns", "resultColumns"]}
                        control={() => {
                            return (
                                <RuleEditorRender
                                    columnCount={
                                        ruleDefinition.columns.length + ruleDefinition.resultColumns.length + 1
                                    }
                                >
                                    <ValueRuleTableHeader {...props} />

                                    <ObserveState
                                        listenTo={ruleDefinition}
                                        properties={["rows"]}
                                        control={() => {
                                            return (
                                                <>
                                                    {ruleDefinition.rows.map((row, idx) => {
                                                        return (
                                                            <XValueTableRow
                                                                row={row}
                                                                index={idx}
                                                                key={row._id}
                                                                ruleDefinition={ruleDefinition}
                                                                editContext={editContext}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                    <RuleTableEmptyIndexColumn
                                        onClick={onAddNewRow}
                                        data-testid="btn-value-table-add-new-row"
                                    >
                                        <PlusGrayIcon />
                                    </RuleTableEmptyIndexColumn>
                                    {ruleDefinition.columns.map((_, idx) => (
                                        <RuleTableEmptyColumn key={idx} />
                                    ))}

                                    <RuleTableEmptyAddColumn />

                                    {ruleDefinition.resultColumns.map((col) => {
                                        return <RuleTableEmptyColumn key={col._id} />;
                                    })}

                                    <RuleTableEmptyAddColumn />
                                    <RuleTableEmptyAddColumn />

                                    <ObserveState
                                        listenTo={ruleDefinition}
                                        properties={["allowDefaultResults"]}
                                        control={() => {
                                            if (!ruleDefinition.allowDefaultResults) return null;

                                            return (
                                                <>
                                                    <RuleLabelColumn
                                                        colSpan={ruleDefinition.columns.length + 1}
                                                        colStart={1}
                                                    >
                                                        Default Result Values
                                                    </RuleLabelColumn>

                                                    {ruleDefinition.resultColumns.map((rescol) => {
                                                        return (
                                                            <ValueTableResultDefaultCell
                                                                ruleDefinition={ruleDefinition}
                                                                resultColumn={rescol}
                                                                editContext={editContext}
                                                                key={rescol._id}
                                                            />
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                </RuleEditorRender>
                            );
                        }}
                    />
                )}
                sidebarWidth={350}
                open={openSettingsLayer}
                onClose={() => setOpenSettingsLayer(false)}
            />

            <ObserveMultiState
                listenTo={[
                    ...props.ruleDefinition.columns,
                    ...props.ruleDefinition.resultColumns,
                    props.ruleDefinition,
                ]}
                control={() => <ValidationInformation provider={ruleDefinition} />}
            />

            <ObserveState
                listenTo={editContext}
                control={() => {
                    return <Notification editContext={editContext} />;
                }}
            />

            <LinkingDataDialog editContext={editContext} />

            <ObserveState
                listenTo={props.editContext}
                properties={["deletingColumn"]}
                control={() => {
                    if (props.editContext.deletingColumn) {
                        return (
                            <ConfirmDialog
                                open={true}
                                onConfirm={(dontAskAgain) => {
                                    if (dontAskAgain) {
                                        DataUtilities.setCookieValue("VALUE_RULE_DELETE_CONFIRM", "true");
                                    }

                                    props.ruleDefinition.removeColumn(props.editContext.deletingColumn!._id);
                                    StateManager.propertyChanged(props.ruleDefinition, "columns");
                                    props.editContext.deletingColumn = undefined;
                                }}
                                onCancel={() => (props.editContext.deletingColumn = undefined)}
                            />
                        );
                    }
                    return null;
                }}
            />
        </RuleDesignerRender>
    );
};
